import type { AxisSweepBatchResult, AxisSweepSubjectType } from './api';

/**
 * ADMIN-SWEEP (2026-07-06) — pure state/reducer core behind the "Corpus
 * tagging sweep" admin card (Settings.tsx). Kept framework-free (no React)
 * so the loop's accumulation/termination logic is directly unit-testable,
 * the SAME house pattern collapsibleSection.ts / alignmentReviewProgress.ts
 * establish for "extract the pure decision, let the hook be a thin caller."
 *
 * The card drives ONE subjectType's sweep at a time by calling
 * POST /api/admin/axis-affinities/backfill in a loop until the server's own
 * `isLastBatch` is true (AxisAffinityTaggerService.ts's resumable-batch
 * contract — see api.ts's runAxisAffinityBackfillBatch doc comment). This
 * module owns:
 *   - the running totals across batches (accumulateBatch) — summed from the
 *     SERVED per-batch numbers only, never invented or estimated ahead of
 *     what the server has actually reported;
 *   - the loop's own idle/running/done/error phase (a tiny state machine,
 *     mirroring useSettings.ts's per-action phase machines);
 *   - the termination check (shouldContinueSweep) — purely `!isLastBatch`,
 *     named so the call site never has to remember the polarity.
 */

/**
 * Card render gate — Settings.tsx renders the "Corpus tagging sweep" card
 * ONLY when GET /auth/me served admin:true on the current session's user.
 * Pure + exported so the gating decision is unit-tested directly (this
 * repo's vitest run is plain Node with no DOM — see root vitest.config.ts's
 * own header comment — so Settings.tsx itself is never rendered in a test;
 * the SAME "extract the pure decision" discipline useAuth.signOut.test.ts's
 * own doc comment names is applied here). Absent `admin` (undefined) reads
 * exactly like `false` — this app never infers admin-ness on its own, only
 * a server-served `true` ever shows the card.
 */
export function shouldRenderAxisSweepSection(user: { admin?: boolean } | null): boolean {
  return user?.admin === true;
}

export type AxisSweepPhase = 'idle' | 'running' | 'done' | 'error';

/** Running totals for ONE subjectType's sweep, accumulated batch-by-batch —
 *  every field here is a SUM of real served AxisSweepBatchResult numbers,
 *  never a client-side estimate or projection. */
export interface AxisSweepTotals {
  batches: number;
  processed: number;
  tagged: number;
  skippedTerminalOnly: number;
  totalDropped: number;
}

export interface AxisSweepRunState {
  phase: AxisSweepPhase;
  totals: AxisSweepTotals;
  error: string | null;
}

export const ZERO_AXIS_SWEEP_TOTALS: AxisSweepTotals = {
  batches: 0,
  processed: 0,
  tagged: 0,
  skippedTerminalOnly: 0,
  totalDropped: 0,
};

export function initialAxisSweepRunState(): AxisSweepRunState {
  return { phase: 'idle', totals: { ...ZERO_AXIS_SWEEP_TOTALS }, error: null };
}

/**
 * Fold ONE served batch result into the running totals — pure, returns a NEW
 * totals object (never mutates the input), mirroring toggleSection's own
 * "new object, not a mutation" discipline. Every batch's processed/tagged/
 * skippedTerminalOnly/totalDropped is summed verbatim; `batches` increments
 * by exactly 1 per call, matching "one call = one batch" 1:1.
 */
export function accumulateBatch(
  totals: AxisSweepTotals,
  batch: AxisSweepBatchResult,
): AxisSweepTotals {
  return {
    batches: totals.batches + 1,
    processed: totals.processed + batch.processed,
    tagged: totals.tagged + batch.tagged,
    skippedTerminalOnly: totals.skippedTerminalOnly + batch.skippedTerminalOnly,
    totalDropped: totals.totalDropped + batch.totalDropped,
  };
}

/** Whether the loop should fire ANOTHER batch call — named (rather than the
 *  caller reading `!batch.isLastBatch` inline) so the polarity is never
 *  second-guessed at the call site. */
export function shouldContinueSweep(batch: AxisSweepBatchResult): boolean {
  return !batch.isLastBatch;
}

/** The honest, ready-to-render one-line summary once a subjectType's sweep
 *  reaches its last batch — states the drop count PLAINLY (never buried or
 *  omitted) per the brief's "a final honest summary incl. the drop count."
 *  Pure derivation, unit-tested directly against every totals shape rather
 *  than only rendered inline in JSX. */
export function describeAxisSweepSummary(
  subjectType: AxisSweepSubjectType,
  totals: AxisSweepTotals,
): string {
  const noun = subjectType === 'achievement' ? 'achievements' : 'facets';
  const batchWord = totals.batches === 1 ? 'batch' : 'batches';
  const base = `Swept ${totals.processed} ${noun} across ${totals.batches} ${batchWord} — ${totals.tagged} tagged, ${totals.skippedTerminalOnly} already-terminal skipped`;
  return totals.totalDropped > 0
    ? `${base}, ${totals.totalDropped} dropped (ungrounded — never invented).`
    : `${base}, 0 dropped.`;
}
